<template>
	<div class="wall-leaderboard">
		<div ref="main" class="wall-leaderboard__main" :class="{ faded }">
			<transition name="fade" mode="out-in">
				<template v-if="showLeaderboard">
					<LeaderboardGrid
						v-if="type === types.grid"
						ref="leaderboard"
						wall
						:results="cachedResults"
						@updated="doLayout"
						@view="onView"
					/>
					<Leaderboard
						v-else
						ref="leaderboard"
						wall
						:results="cachedResults"
						@updated="doLayout"
						@view="onView"
					/>
				</template>
				<div v-else-if="!loading" class="wall-leaderboard__empty">
					<h2>Get things started by voting!</h2>
				</div>
			</transition>
		</div>
		<!-- Loading overlay -->
		<transition name="fade">
			<Loading v-if="loading" color="white" />
		</transition>
		<!-- Submission overlay -->
		<transition name="fade">
			<PolaroidView
				v-if="viewing"
				wall
				:submission="viewing"
				@dismiss="dismiss"
			/>
		</transition>
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { enums } from 'protoswipe-shared'
import Loading from '@/components/shared/Loading'
import Leaderboard from '@/components/main/Leaderboard'
import LeaderboardGrid from '@/components/main/LeaderboardGrid'
import PolaroidView from '@/components/main/PolaroidView'

const { LeaderboardType } = enums

const VIEW_TIMEOUT = 20000
const MAX_RESULTS = 10

export default {
	name: 'WallLeaderboard',
	props: {
		mouse: Object
	},
	data() {
		return {
			cachedResults: [],
			numResults: MAX_RESULTS,
			viewingId: null,
			timeout: null,
			interval: null,
			types: LeaderboardType
		}
	},
	computed: {
		...mapState({
			loading: s => s.leaderboard.loading,
			fetchedAll: s => s.leaderboard.done,
			type: s => s.campaign.campaign.wallLeaderboardType || LeaderboardType.list
		}),
		...mapGetters({
			results: 'leaderboard/results',
			refreshMs: 'config/leaderboardRefreshMs'
		}),
		showLeaderboard() {
			return this.results.length || this.cachedResults.length
		},
		viewing() {
			return this.cachedResults.find(r => r.id === this.viewingId)
		},
		faded() {
			return Boolean(this.loading || this.viewing)
		}
	},
	methods: {
		...mapActions({
			refreshConfig: 'config/get',
			refreshCampaign: 'campaign/load',
			fetchLeaderboard: 'leaderboard/fetch'
		}),
		cacheResults() {
			this.cachedResults = [...this.results].slice(0, this.numResults)
		},
		async load() {
			// Refresh config
			await Promise.all([this.refreshConfig(), this.refreshCampaign()])

			// Save last results before force reloading
			const success = await this.fetchLeaderboard({ force: true })
			if (success) {
				this.numResults = MAX_RESULTS
				this.cacheResults()
			}
		},
		doLayout() {
			// Remove results until everything fits into container height
			const h = this.$refs.main.offsetHeight
			const lbh = this.$refs.leaderboard.$el.offsetHeight
			if (lbh > h && this.numResults > 1) {
				this.numResults--
				this.cacheResults()
			}
		},
		onView(id) {
			this.viewingId = id
			this.setViewTimeout()
		},
		clearViewTimeout() {
			window.clearTimeout(this.timeout)
		},
		setViewTimeout() {
			this.clearViewTimeout()
			this.timeout = window.setTimeout(this.dismiss, VIEW_TIMEOUT)
		},
		dismiss() {
			this.viewingId = null
		}
	},
	watch: {
		mouse() {
			if (this.viewingId !== null) this.setViewTimeout()
		},
		refreshMs(next) {
			window.clearInterval(this.interval)
			this.interval = window.setInterval(this.load, next)
		}
	},
	created() {
		this.load()
		// Set up loading interval
		this.interval = window.setInterval(this.load, this.refreshMs)
	},
	beforeDestroy() {
		this.clearViewTimeout()
		window.clearInterval(this.interval)
	},
	components: {
		Loading,
		Leaderboard,
		LeaderboardGrid,
		PolaroidView
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/transitions';

@include fade($spd-wall);

.wall-leaderboard {
	@extend %flex-col;
	flex-grow: 1;
	max-height: 100vh;

	&__main {
		@extend %flex-center;
		flex-grow: 1;
		overflow: hidden;
		padding: 0 2rem;
		justify-content: stretch;
		transition: opacity $spd-wall;
		&.faded {
			opacity: 0.25;
		}
	}
	&__view {
		@extend %abs-full;
	}
	&__empty {
		color: $white;
		width: 100%;
		text-align: center;
	}
}
</style>
